import { useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";
import { useLocalization } from "@/hooks/useLocalization";

export default function CategoryBrowse() {
  const { t } = useTranslation();
  const { lang } = useLocalization();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCat, setSelectedCat] = useState("");

  const handleSelect = (catId: number) => {
    const cat = categories.find((c) => c.id === catId);
    if (cat) {
      window.open(cat.url, "_blank", "noopener,nofollow");
    }
    setSelectedCat("");
    setDropdownOpen(false);
  };

  return (
    <section id="categories" className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
          {/* Left: Title + dropdown */}
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold tracking-wider uppercase text-primary-600">
              {t("categories.badge")}
            </span>
            <h2 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              {t("categories.title")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t("categories.subtitle")}
            </p>

            <div className="relative mt-6">
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                className="flex w-full max-w-sm items-center justify-between rounded-xl bg-background-100 border border-background-200/70 px-4 py-3 text-sm text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-search-line"></i>
                  </span>
                  {selectedCat
                    ? categories.find((c) => c.id === selectedCat)?.name[lang] || t("dropdown.placeholder")
                    : t("dropdown.placeholder")}
                </span>
                <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className={`${dropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-full max-w-sm rounded-xl bg-white shadow-xl border border-background-200/70 overflow-hidden z-20">
                  <div className="max-h-60 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleSelect(cat.id)}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-primary-600">
                          <i className={`${cat.icon} text-sm`}></i>
                        </span>
                        <span className="font-medium">{cat.name[lang]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Category grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex flex-col items-center gap-2.5 rounded-xl bg-background-100 border border-background-200/40 px-2 py-4 hover:border-primary-300/60 hover:bg-primary-50/50 transition-all cursor-pointer"
                >
                  <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-background-50 border border-background-200/60 text-primary-600 group-hover:border-primary-200 group-hover:text-primary-700 transition-colors">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </span>
                  <span className="text-center text-xs font-medium text-foreground-700 group-hover:text-primary-700 leading-tight">
                    {cat.name[lang]}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}